import { Currency, currencyToCode } from './currency';

/**
 * Type guard to check if a value is a Currency enum value.
 *
 * @param value - Value to check
 * @returns True if value is a Currency enum value
 */
function isCurrencyEnum(value: Currency | string): value is Currency {
  return Object.values<string>(Currency).includes(value);
}

/**
 * Extract the currency symbol from a currency code.
 *
 * Uses Intl.NumberFormat to determine the currency symbol for a given currency
 * code and locale. Returns the symbol used by the locale's number formatting.
 *
 * @param currency - Currency enum value or ISO 4217 currency code string (e.g., Currency.USD, 'USD', 'EUR')
 * @param locale - BCP 47 locale string (e.g., 'en-US', 'en-GB'). Defaults to 'en-US'
 * @returns Currency symbol string (e.g., '$', '€', '£'). Falls back to currency code if symbol cannot be determined
 *
 * @throws {RangeError} If currency is not a valid ISO 4217 code (e.g., Currency.EssensysCredits or invalid string). Note: Currency.UNKNOWN is automatically converted to 'USD' and will not throw.
 *
 * @example
 * ```ts
 * getCurrencySymbol(Currency.USD);           // "$"
 * getCurrencySymbol(Currency.EUR);           // "€"
 * getCurrencySymbol(Currency.GBP);           // "£"
 * getCurrencySymbol('CAD');                  // "$"
 * getCurrencySymbol(Currency.GBP, 'en-GB');  // "£"
 * getCurrencySymbol(Currency.UNKNOWN);       // "$" (converted to USD)
 * ```
 */
export function getCurrencySymbol(
  currency: Currency | string,
  locale: string = 'en-US'
): string {
  // Convert Currency enum to code string if needed
  // Check if the value is a Currency enum value (even if it's a string at runtime)
  const currencyCode = isCurrencyEnum(currency)
    ? currencyToCode(currency)
    : currency;

  // Use Intl.NumberFormat to extract the currency symbol
  const numberFormat = new Intl.NumberFormat(locale, {
    currency: currencyCode,
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
  });

  // Format a small amount and extract the symbol from the beginning
  const formatted = numberFormat.format(0.01);
  // Remove digits, decimal points, and spaces to get just the symbol
  const symbol = formatted.replace(/[\d\s.,]/g, '');

  return symbol || currencyCode;
}
